import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  signal,
  inject,
  input,
  contentChild,
} from '@angular/core';
import { ButtonType, ButtonSizeType } from '../../../types';
import { IconUIComponent } from '../icon/icon.component';

@Component({
  selector: 'fr8bot-ui-button',
  imports: [],
  templateUrl: './button.component.html',
  styleUrl: './button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ButtonUIComponent implements AfterViewInit {
  private element = inject(ElementRef);

  readonly type = input<ButtonType>('primary');
  readonly size = input<ButtonSizeType>('medium');
  readonly disabled = input(false);
  readonly nativeType = input<'button' | 'submit' | 'reset'>('button');
  /** Drops the horizontal padding; the button still keeps its min-width (equal to its height). */
  readonly noPadding = input(false);

  private readonly iconRef = contentChild(IconUIComponent);

  protected isIconButton = signal(false);

  protected readonly buttonTypeMap: Record<ButtonType, ButtonType[keyof ButtonType]> = {
    primary: 'button-primary',
    primaryCut: 'button-primary-cut',
    secondary: 'button-secondary',
    transparent: 'button-transparent',
    semiTransparent: 'button-semi-transparent',
  };

  protected readonly buttonSizeMap: Record<ButtonSizeType, ButtonSizeType[keyof ButtonSizeType]> = {
    xs: 'button-xs',
    small: 'button-small',
    medium: 'button-medium',
    large: 'button-large',
  };

  ngAfterViewInit(): void {
    if (this.hasOwnText()) {
      return;
    }

    const hasIcon = !!this.iconRef();
    const hasPrefixIcon = !!this.element.nativeElement.querySelector('[prefix-icon]');
    const hasPostfixIcon = !!this.element.nativeElement.querySelector('[postfix-icon]');

    this.isIconButton.set(hasIcon && !hasPrefixIcon && !hasPostfixIcon);
  }

  /**
   * Text outside the icons. A font icon renders its glyph as text, so the host's own text would
   * count every icon as a label and pad icon-only buttons.
   */
  private hasOwnText(): boolean {
    const host: HTMLElement = this.element.nativeElement;
    const walker = host.ownerDocument.createTreeWalker(host, NodeFilter.SHOW_TEXT);

    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      if (node.textContent?.trim() && !node.parentElement?.closest('fr8bot-ui-icon')) {
        return true;
      }
    }

    return false;
  }
}
